import { cn } from "@/common/utils/cn"
import { replacePeriodToUnderscore } from "@/common/utils/transformers"
import { Popover, PopoverContent, PopoverTrigger } from "@/common/components/ui/popover"
import { ClockIcon } from "lucide-react"
import { Dispatch, SetStateAction } from "react"

interface TimeOptionsPopoverProps {
  fieldName: string
  isPopoverOpen: boolean;
  setIsPopoverOpen: Dispatch<SetStateAction<boolean>>;
  setSelectedHour: Dispatch<SetStateAction<string | undefined>>;
  hours: string[];
  minutes: string[];
  disabled?: boolean;
  activeHour?: string;
  currentMinute?: string;
  handleMinuteSelect: (minute: string) => void;
  onHourSelect: (hour: string) => void;
}

export default function TimeOptionsPopover({
  fieldName,
  isPopoverOpen,
  setIsPopoverOpen,
  setSelectedHour,
  hours,
  minutes,
  disabled,
  activeHour,
  currentMinute,
  handleMinuteSelect,
  onHourSelect,
}: TimeOptionsPopoverProps) {
  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={(open) => {
        setIsPopoverOpen(open)
        if (!open) setSelectedHour(undefined)
      }}
    >
      <PopoverTrigger
        data-testid={`${replacePeriodToUnderscore(fieldName)}_time_trigger`}
        asChild
        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
        disabled={disabled}
      >
        <button
          type="button"
          className="flex items-center justify-center w-6 h-6 cursor-pointer group disabled:cursor-default"
          disabled={disabled}
        >
          <ClockIcon
            strokeWidth={1.5}
            className="w-4 h-4 text-gray-400 hover:text-gray-500 group-disabled:hover:text-gray-400"
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-auto p-0 bg-[#212023] border-neutral-op-8 dark"
        align="start"
      >
        <div className="flex">
          <div className="flex flex-col border-r border-neutral-op-8 w-12">
            <p className="text-xs text-white-400 text-center py-2 border-b border-neutral-op-8">
              Hora
            </p>
            <div
              className="overflow-y-auto max-h-36 flex flex-col py-1 min-h-0"
              onWheel={(e) => { e.currentTarget.scrollTop += e.deltaY }}
            >
              {hours.map((hour) => (
                <button
                  key={hour}
                  type="button"
                  onClick={() => onHourSelect(hour)}
                  className={cn(
                    'text-sm px-0 py-1.5 text-white-800 hover:bg-white-100 text-center',
                    activeHour === hour && 'bg-primary-blue text-white hover:bg-primary-blue'
                  )}
                >
                  {hour}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col w-12">
            <p className="text-xs text-white-400 text-center py-2 border-b border-neutral-op-8">
              Min
            </p>
            <div className="flex flex-col py-1">
              {minutes.map((minute) => (
                <button
                  key={minute}
                  type="button"
                  onClick={() => handleMinuteSelect(minute)}
                  className={cn(
                    'text-sm px-0 py-1.5 text-white-800 hover:bg-white-100 text-center',
                    currentMinute === minute && 'bg-primary-blue text-white hover:bg-primary-blue'
                  )}
                >
                  {minute}
                </button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
